import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  ImageData, 
  PipelineStep, 
  StatusMessage, 
  ProcessedResult, 
  UIState,
  ProcessingCache,
  ProcessingTiming,  // Add this import
  DEBOUNCE_DELAY 
} from '../types';
import { ApiService } from '../services/api';
import { SessionUtils, ThemeUtils, debounce } from '../utils';

/**
 * Session management hook - CLEANUP DISABLED for testing
 * Images will only be deleted on logout or manual cleanup
 */
export function useSession() {
  const [sessionId] = useState(() => SessionUtils.getSessionId());
  
  // DISABLED: Tab-close cleanup is causing issues with reload detection
  // TODO: Re-enable once we have a reliable way to detect actual close vs refresh
  
  useEffect(() => {
    console.log('ℹ️ Session initialized:', sessionId);
    console.log('ℹ️ Tab-close cleanup is DISABLED - images persist across reloads');
    
    // Cleanup on component unmount is disabled
    return () => {
      console.log('ℹ️ Session hook unmounting - no cleanup triggered');
    };
  }, [sessionId]);
  
  return { sessionId };
}

/**
 * Session heartbeat hook - Keeps session active with periodic pings
 */
export function useSessionHeartbeat() {
  const { sessionId } = useSession();
  const [isActive, setIsActive] = useState(true);
  
  useEffect(() => {
    // Send initial heartbeat immediately
    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('pixelflow_access_token');
        const headers: any = { 'Content-Type': 'application/json' };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/images/heartbeat`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ session_id: sessionId })
        });
        
        const data = await response.json();
        
        if (data.success) {
          if (data.created) {
            console.log('❤️✨ Heartbeat created new session:', sessionId);
          } else {
            console.log('❤️ Heartbeat: session active');
          }
          setIsActive(true);
        } else {
          console.warn('⚠️ Heartbeat failed:', data.message);
          setIsActive(false);
        }
      } catch (error) {
        console.error('❌ Heartbeat error:', error);
        setIsActive(false);
      }
    };
    
    // Send heartbeat immediately on mount
    sendHeartbeat();
    
    // Then send heartbeat every 30 seconds
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);
    
    console.log('❤️ Heartbeat system started - pinging every 30 seconds');
    
    return () => {
      clearInterval(heartbeatInterval);
      console.log('❤️ Heartbeat system stopped');
    };
  }, [sessionId]);
  
  return { sessionId, isActive };
}

/**
 * Theme management hook
 */
export function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => ThemeUtils.getTheme());
  
  const toggleTheme = useCallback(() => {
    const newTheme = ThemeUtils.toggleTheme();
    setTheme(newTheme);
  }, []);
  
  useEffect(() => {
    ThemeUtils.setTheme(theme);
  }, [theme]);
  
  return { theme, toggleTheme, setTheme };
}

/**
 * Status message management hook
 */
export function useStatus() {
  const [status, setStatus] = useState<StatusMessage>({ text: '', type: 'info' });
  const timeoutRef = useRef<NodeJS.Timeout>();
  
  const updateStatus = useCallback((text: string, type: StatusMessage['type'] = 'info', persistent = false) => {
    clearTimeout(timeoutRef.current);
    setStatus({ text, type, persistent });
    
    // Clear success/error messages after 4 seconds, but keep persistent messages
    if (!persistent && ['success', 'error'].includes(type)) {
      timeoutRef.current = setTimeout(() => {
        // Don't clear the message, instead trigger a re-evaluation of the default state
        // This will be handled by the useEffect in App.tsx
        setStatus({ text: '', type: 'info' });
      }, 4000);
    }
  }, []);
  
  const clearStatus = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setStatus({ text: '', type: 'info' });
  }, []);
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return { status, updateStatus, clearStatus };
}

/**
 * Image management hook with database persistence
 */
export function useImages() {
  const [images, setImages] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState(false);
  const { sessionId } = useSession();
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Load images from database on mount
  useEffect(() => {
    const loadImagesFromDB = async () => {
      if (!isInitialized) {
        try {
          console.log('Loading images from database for session:', sessionId);
          const savedImages = await ApiService.getSessionImages(sessionId);
          if (savedImages.length > 0) {
            console.log('Restored images from database:', savedImages.length);
            setImages(savedImages);
          }
        } catch (error) {
          console.error('Error loading images from database:', error);
          // Continue with empty images if load fails
        }
        setIsInitialized(true);
      }
    };
    
    loadImagesFromDB();
  }, [sessionId, isInitialized]);
  
  const addImages = useCallback(async (files: File[]) => {
    setLoading(true);
    try {
      const uploadPromises = files.map(file => ApiService.uploadImage(file, sessionId));
      const responses = await Promise.allSettled(uploadPromises);
      
      const newImages: ImageData[] = [];
      const fileReadPromises: Promise<void>[] = [];
      let quotaError: any = null;
      const errors: string[] = [];
      
      responses.forEach((response, index) => {
        if (response.status === 'fulfilled' && response.value.success && response.value.image) {
          const imageData: ImageData = {
            ...response.value.image,
            selected: true,
            file: files[index],
            thumbnailDataUrl: response.value.thumbnail,
            dataUrl: '' // Initialize as empty, will be filled by FileReader
          };
          
          // Create a promise for the FileReader
          const fileReadPromise = new Promise<void>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              if (e.target?.result) {
                imageData.dataUrl = e.target.result as string;
                console.log('Full dataUrl created for:', files[index].name, 'Size:', imageData.dataUrl.length);
              }
              resolve();
            };
            reader.onerror = () => {
              console.error('FileReader error for:', files[index].name);
              resolve(); // Resolve anyway to not block
            };
            reader.readAsDataURL(files[index]);
          });
          
          fileReadPromises.push(fileReadPromise);
          newImages.push(imageData);
        } else if (response.status === 'rejected') {
          // Track failed uploads
          const error = response.reason;
          console.error(`Failed to upload ${files[index].name}:`, error);
          errors.push(`${files[index].name}: ${error.message || 'Upload failed'}`);
          
          // Check if it's a quota error (507) or image limit error (400)
          if (error.response?.status === 507) {
            // Storage quota exceeded
            quotaError = error.response.data.detail;
          } else if (error.response?.status === 400 && error.response?.data?.detail?.error === 'Image limit reached') {
            // Image count limit reached
            const err: any = new Error(error.response.data.detail.message);
            err.response = error.response;
            throw err;
          }
        }
      });
      
      // If there were errors, log them
      if (errors.length > 0) {
        console.error('Upload errors:', errors);
        throw new Error(`Failed to upload ${errors.length} image(s). Check console for details.`);
      }
      
      // If quota exceeded, throw error to show popup
      if (quotaError) {
        const error: any = new Error(quotaError.message || 'Storage quota exceeded');
        error.quotaInfo = quotaError;
        throw error;
      }
      
      // Wait for all FileReaders to complete
      await Promise.all(fileReadPromises);
      
      console.log('All images processed:', newImages.map(img => ({
        filename: img.filename,
        hasThumbnail: !!img.thumbnailDataUrl,
        hasFullImage: !!img.dataUrl,
        fullImageSize: img.dataUrl?.length || 0
      })));
      
      setImages(prev => [...prev, ...newImages]);
      return newImages;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);
  
  const removeImage = useCallback(async (imageId: string) => {
    // Remove from UI immediately
    setImages(prev => prev.filter(img => img.id !== imageId));
    
    // Delete from database
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/images/image/${imageId}`, {
        method: 'DELETE'
      });
      console.log('Image deleted from database:', imageId);
    } catch (error) {
      console.error('Failed to delete image from database:', error);
      // Image already removed from UI, so we can continue
    }
  }, []);
  
  const toggleImageSelection = useCallback((imageId: string) => {
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, selected: !img.selected } : img
    ));
  }, []);
  
  const selectAllImages = useCallback(() => {
    setImages(prev => prev.map(img => ({ ...img, selected: true })));
  }, []);
  
  const deselectAllImages = useCallback(() => {
    setImages(prev => prev.map(img => ({ ...img, selected: false })));
  }, []);
  
  const selectedImages = useMemo(() => images.filter(img => img.selected), [images]);
  const selectedCount = selectedImages.length;
  
  return {
    images,
    selectedImages,
    selectedCount,
    loading,
    addImages,
    removeImage,
    toggleImageSelection,
    selectAllImages,
    deselectAllImages
  };
}

/**
 * Pipeline management hook
 */
export function usePipeline() {
  const [pipeline, setPipeline] = useState<PipelineStep[]>([]);
  const [history, setHistory] = useState<PipelineStep[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  const saveToHistory = useCallback((newPipeline: PipelineStep[]) => {
    // Don't save if pipeline hasn't actually changed
    const currentPipeline = history[historyIndex] || [];
    if (JSON.stringify(currentPipeline) === JSON.stringify(newPipeline)) {
      return;
    }
    
    // Remove any future history when making a new change
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newPipeline]);
    
    // Limit history to last 50 actions to prevent memory issues
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(newHistory.length - 1);
    }
    
    setHistory(newHistory);
  }, [history, historyIndex]);
  
  const addStep = useCallback((operationName: string, params: Record<string, any> = {}) => {
    const newStep: PipelineStep = {
      id: `step_${Date.now()}`,
      name: operationName,
      params
    };
    const newPipeline = [...pipeline, newStep];
    setPipeline(newPipeline);
    saveToHistory(newPipeline);
  }, [pipeline, saveToHistory]);
  
  const removeStep = useCallback((index: number) => {
    const newPipeline = pipeline.filter((_, i) => i !== index);
    setPipeline(newPipeline);
    saveToHistory(newPipeline);
  }, [pipeline, saveToHistory]);
  
  const updateStepParam = useCallback((stepId: string, paramName: string, value: any) => {
    const newPipeline = pipeline.map(step => 
      step.id === stepId 
        ? { ...step, params: { ...step.params, [paramName]: value } }
        : step
    );
    setPipeline(newPipeline);
    saveToHistory(newPipeline);
  }, [pipeline, saveToHistory]);
  
  const moveStep = useCallback((fromIndex: number, toIndex: number) => {
    const newPipeline = [...pipeline];
    const [movedStep] = newPipeline.splice(fromIndex, 1);
    newPipeline.splice(toIndex, 0, movedStep);
    setPipeline(newPipeline);
    saveToHistory(newPipeline);
  }, [pipeline, saveToHistory]);
  
  const resetPipeline = useCallback(() => {
    const newPipeline: PipelineStep[] = [];
    setPipeline(newPipeline);
    saveToHistory(newPipeline);
  }, [saveToHistory]);
  
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setPipeline([...history[newIndex]]);
    }
  }, [history, historyIndex]);
  
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setPipeline([...history[newIndex]]);
    }
  }, [history, historyIndex]);
  
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  
  const generatePipelineHash = useCallback((imageIds: string[]) => {
    return JSON.stringify({
      imageIds: imageIds.sort(),
      pipeline: pipeline.map(step => ({ name: step.name, params: step.params }))
    });
  }, [pipeline]);
  
  return {
    pipeline,
    addStep,
    removeStep,
    updateStepParam,
    moveStep,
    resetPipeline,
    undo,
    redo,
    canUndo,
    canRedo,
    generatePipelineHash
  };
}
/**
 * Live processing hook with caching
 */
export function useLiveProcessing() {
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [cache, setCache] = useState<ProcessingCache | null>(null);
  const [timingData, setTimingData] = useState<{ total_time: number; step_timings: ProcessingTiming[] }>({ total_time: 0, step_timings: [] });
  const { sessionId } = useSession();
  const abortControllerRef = useRef<AbortController>();
  
  const processLive = useCallback(async (imageId: string, pipeline: PipelineStep[]) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    
    const pipelineHash = JSON.stringify({ imageId, pipeline });
    
    // Check cache
    if (cache && cache.pipelineHash === pipelineHash && 
        Date.now() - cache.timestamp < 10 * 60 * 1000) {
      setResults(cache.results);
      return cache.results;
    }
    
    setLoading(true);
    try {
      const response = await ApiService.processLive({
        image_id: imageId,
        pipeline: pipeline.map(step => ({ name: step.name, params: step.params })),
        session_id: sessionId
      });
      
      if (response.success) {
        setResults(response.results);
        setTimingData({
          total_time: response.total_time || 0,
          step_timings: response.step_timings || []
        });
        
        setCache({
          pipelineHash,
          results: response.results,
          timestamp: Date.now()
        });
        
        return response.results;
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Live processing request aborted');
        return [];
      }
      console.error('Live processing error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [sessionId, cache]);
  
  const debouncedProcessLive = useMemo(
    () => debounce(processLive, DEBOUNCE_DELAY),
    [processLive]
  );
  
  const clearResults = useCallback(() => {
    setResults([]);
    setCache(null);
  }, []);
  
  const invalidateCache = useCallback(() => {
    setCache(null);
  }, []);
  
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  return {
    results,
    loading,
    timingData,
    processLive: debouncedProcessLive,
    clearResults,
    invalidateCache
  };
}

/**
 * Batch processing hook with real-time results and cancellation
 */
export function useBatchProcessing() {
  const [results, setResults] = useState<ProcessedResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [timingData, setTimingData] = useState<{ total_time: number; step_timings: ProcessingTiming[] }>({ total_time: 0, step_timings: [] });
  const { sessionId } = useSession();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isCancelledRef = useRef(false);
  
  const processBatch = useCallback(async (
    imageIds: string[], 
    pipeline: PipelineStep[],
    onProgress?: (current: number, total: number, result: ProcessedResult) => void
  ) => {
    setLoading(true);
    setResults([]);
    setTimingData({ total_time: 0, step_timings: [] });
    isCancelledRef.current = false;
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      const allResults: ProcessedResult[] = [];
      const allTimings: ProcessingTiming[] = [];
      
      // Process images one by one
      for (let i = 0; i < imageIds.length; i++) {
        // Check if cancelled
        if (isCancelledRef.current) {
          console.log('Batch processing cancelled');
          break;
        }
        
        const imageId = imageIds[i];
        
        try {
          const response = await ApiService.processLive({
            image_id: imageId,
            pipeline: pipeline.map(step => ({ name: step.name, params: step.params })),
            session_id: sessionId
          });
          
          if (response.success && response.results.length > 0) {
            const result: ProcessedResult = {
              id: imageId,
              originalUrl: '',
              processedUrl: response.results[response.results.length - 1],
              intermediateResults: response.results
            };
            
            allResults.push(result);
            
            // Add to results immediately (real-time update)
            setResults(prev => [...prev, result]);
            
            // Track timings
            if (response.step_timings) {
              allTimings.push(...response.step_timings);
            }
            
            // Call progress callback
            if (onProgress) {
              onProgress(i + 1, imageIds.length, result);
            }
          }
        } catch (imageError) {
          console.error(`Failed to process image ${i + 1}:`, imageError);
          // Continue with next image
        }
      }
      
      // Update final timing data
      setTimingData({
        total_time: allTimings.reduce((sum, t) => sum + t.duration, 0) / 1000,
        step_timings: allTimings
      });
      
      return allResults;
    } catch (error) {
      console.error('Batch processing error:', error);
      throw error;
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [sessionId]);
  
  const cancelBatch = useCallback(() => {
    isCancelledRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLoading(false);
  }, []);
  
  const setResultsManually = useCallback((newResults: ProcessedResult[]) => {
    setResults(newResults);
  }, []);
  
  const clearResults = useCallback(() => {
    setResults([]);
    setTimingData({ total_time: 0, step_timings: [] });
  }, []);
  
  return {
    results,
    loading,
    timingData,
    processBatch,
    cancelBatch,
    setResultsManually,
    clearResults
  };
}

/**
 * UI state management hook
 */
export function useUI() {
  const [ui, setUI] = useState<UIState>({
    theme: 'dark',
    sidebarCollapsed: false,
    liveProcessingEnabled: false,
    currentView: 'grid',
    inspectorView: 'normal',
    selectedImages: [],
    selectedResult: null,
    viewingStepIndex: -1
  });
  
  const updateUI = useCallback((updates: Partial<UIState>) => {
    setUI(prev => ({ ...prev, ...updates }));
  }, []);
  
  const toggleSidebar = useCallback(() => {
    setUI(prev => ({ ...prev, sidebarCollapsed: !prev.sidebarCollapsed }));
  }, []);
  
  const setLiveProcessing = useCallback((enabled: boolean) => {
    setUI(prev => ({ ...prev, liveProcessingEnabled: enabled }));
  }, []);
  
  const setCurrentView = useCallback((view: UIState['currentView']) => {
    setUI(prev => ({ ...prev, currentView: view }));
  }, []);
  
  const setInspectorView = useCallback((view: UIState['inspectorView']) => {
    setUI(prev => ({ ...prev, inspectorView: view }));
  }, []);
  
  const setViewingStep = useCallback((stepIndex: number) => {
    setUI(prev => ({ ...prev, viewingStepIndex: stepIndex }));
  }, []);
  
  return {
    ui,
    updateUI,
    toggleSidebar,
    setLiveProcessing,
    setCurrentView,
    setInspectorView,
    setViewingStep
  };
}

/**
 * Modal management hook
 */
export function useModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<any>(null);
  
  const openModal = useCallback((modalData?: any) => {
    setData(modalData || null);
    setIsOpen(true);
  }, []);
  
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);
  
  const toggleModal = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  
  return {
    isOpen,
    data,
    openModal,
    closeModal,
    toggleModal
  };
}

/**
 * Keyboard shortcuts hook
 */
export function useKeyboardShortcuts(shortcuts: Record<string, () => void>) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const ctrlKey = event.ctrlKey || event.metaKey;
      
      let shortcutKey = '';
      if (ctrlKey) shortcutKey += 'ctrl+';
      if (event.shiftKey) shortcutKey += 'shift+';
      if (event.altKey) shortcutKey += 'alt+';
      shortcutKey += key;
      
      if (shortcuts[shortcutKey]) {
        event.preventDefault();
        shortcuts[shortcutKey]();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
}

/**
 * Resize observer hook
 */
export function useResizeObserver(callback: (entry: ResizeObserverEntry) => void) {
  const elementRef = useRef<HTMLElement>();
  
  useEffect(() => {
    if (!elementRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) callback(entries[0]);
    });
    
    observer.observe(elementRef.current);
    
    return () => observer.disconnect();
  }, [callback]);
  
  return elementRef;
}
